'use client';

import NumberFlow from '@number-flow/react';
import { useLocale } from 'next-intl';
import { ComponentPropsWithoutRef, type CSSProperties } from 'react';
import { Currency } from '@/shared/model/Currency';

type AnimatedPriceProps = {
    amount: number;
    currency: Currency;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    className?: string;
} & Omit<ComponentPropsWithoutRef<typeof NumberFlow>, 'value' | 'format' | 'locales'>;

// Off-screen, non-announced style for the plain-text mirror below.
const SR_HIDDEN_STYLE: CSSProperties = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
};

export const AnimatedPrice = ({
    amount,
    currency,
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    className,
    ...props
}: AnimatedPriceProps) => {
    const locale = useLocale();

    return (
        <>
            <NumberFlow
                value={amount}
                locales={locale}
                format={{
                    style: 'currency',
                    currency,
                    minimumFractionDigits,
                    maximumFractionDigits,
                }}
                className={className}
                transformTiming={{ duration: 100, easing: 'ease' }}
                spinTiming={{ duration: 200, easing: 'ease' }}
                opacityTiming={{ duration: 350, easing: 'ease' }}
                {...props}
            />
            {/*
             * Plain-text mirror of the animated value. NumberFlow renders its digits
             * inside an OPEN shadow root, so the light-DOM textContent of the price
             * cell is otherwise empty — breaking `.textContent()`-based reads (e2e
             * tests, copy/select). The identical locale + format keeps it byte-for-
             * byte equal to the animated value. aria-hidden because NumberFlow already
             * exposes the value as its accessible name (no double-announce).
             */}
            <span aria-hidden="true" style={SR_HIDDEN_STYLE}>
                {new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency,
                    minimumFractionDigits,
                    maximumFractionDigits,
                }).format(amount)}
            </span>
        </>
    );
};
